import { translateBackend } from '@assets/i18n/config';
import type { TranslatedString } from '@interfaces/shared';
import type { Transfer } from '@interfaces/transfer';
import { CheckCircle, Error, ExpandMore } from '@mui/icons-material';
import { Box } from '@ovComponents/1-primative/box/box';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from '@ovComponents/2-component/accordion/accordion';
import { useThemeTokens } from '@providers/themeTokenProvider';
import { useTranslation } from 'react-i18next';

type AlertReportRule = {
  type: string;
  translatedDescription: TranslatedString;
};

export const SuspiciousActivityAccordion = ({
  transfer,
}: {
  transfer: Transfer;
}) => {
  const { t } = useTranslation(['components', 'shared']);
  const { sys } = useThemeTokens();

  return (
    <Accordion
      sx={{
        boxShadow: 'none !important',
        backgroundColor: transfer?.isSuspicious
          ? sys.color.negativeVariant
          : sys.color.positiveVariant,
        border: `1px solid ${transfer?.isSuspicious ? sys.color.negativeVariantPressed : sys.color.positiveVariantPressed} !important`,
        borderRadius: `${sys.borderRadius.md} !important`,
        overflow: 'hidden',
        '& .MuiSvgIcon-root': { width: '20px' },
        '& .MuiAccordionSummary-root': {
          borderTop: 'none !important',
        },
      }}
      multiple
    >
      <AccordionSummary
        expandIcon={
          transfer?.isSuspicious && (
            <ExpandMore sx={{ color: sys.color.onSurface }} />
          )
        }
      >
        <Box display='flex' alignItems='center' gap='8px'>
          {transfer?.isSuspicious ? (
            <Error color='error' />
          ) : (
            <CheckCircle color='success' />
          )}
          <Typography variant='bodyLarge' weight='bold'>
            {transfer?.isSuspicious
              ? t('components:suspiciousActivityDetected')
              : t('components:noSuspiciousActivity')}
          </Typography>
        </Box>
      </AccordionSummary>
      {transfer?.alertReport && (
        <AccordionDetails sx={{ py: 0, pb: 0.5 }}>
          {transfer?.alertReport?.rules.map(
            (rule: AlertReportRule, index: number) => (
              <Box
                display='flex'
                justifyContent='space-between'
                alignItems='center'
                py={1}
                borderBottom={
                  index !== (transfer?.alertReport?.rules.length ?? 0) - 1
                    ? `1px solid ${sys.color.negativeVariantHover}`
                    : 'none'
                }
              >
                <Typography variant='bodySmall' weight='bold'>
                  {t(`components:ruleTypes.${rule.type}`)}
                </Typography>
                <Typography variant='bodySmall' textAlign='right'>
                  {translateBackend(rule.translatedDescription)}
                </Typography>
              </Box>
            ),
          )}
        </AccordionDetails>
      )}
    </Accordion>
  );
};
